import '../../common/polyfills'
import React from 'react'
import { hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { tag } from '@pcln/measure'
import PCLNAnalytics from '@pcln/analytics'
import ListingsClientV2 from './listings-client-v2'
import { wrapPromiseForStreaming } from './util/wrapPromiseForStreaming'
import { configureStore } from './store'
import { listingsApp } from './reducers'
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client'
import { AirSearchRequestModel } from './services/airSearch/airSearchRequest/models/airSearchRequestModel'
import { logRetailPriceWithAPGData } from './util/logRetailPriceWithAPGData'
import pclnExpt from '@pcln/experimentation'
import { Provider } from 'react-redux'
import { getGraphURL } from '../utils/getServiceUrl'
import '@mfs/common/tailwind.css'
import { APOLLO_CLIENT_NAME } from '../common/constants/constants'

const pclnBootstrapData = window.__PRELOADED_STATE__.pclnBootstrapData
const { requestLocals, searchRequest } = pclnBootstrapData ?? {}
const { referral, seti, webstats } = requestLocals ?? {}
const appName = pclnBootstrapData?.isPackageListings ? 'package-search' : 'fly-search'
const { airSearchRequestModel: serverAirSearchRequestModel } = window.__PRELOADED_STATE__ || {}

const airSearchRequestModel = new AirSearchRequestModel(serverAirSearchRequestModel)

const { listings, listingsMetaData } = window.__PRELOADED_STATE__ || {}

const store = configureStore(listingsApp(), {
  ...window.__PRELOADED_STATE__,
  airSearchPromise: wrapPromiseForStreaming(
    new Promise((resolve) => {
      resolve()
    })
  ),
  airSearchRequestModel,
  // Initialize employee discount with initial session key
  employeeDiscount: {
    isEnabled: false,
    standardSessionKey: airSearchRequestModel?.searchSessionKey || '',
    employeeSessionKey: '',
  },
})

if (webstats?.clientGUID) {
  const experimentDetails = seti?.map(({ setiTagName, setiVariantName, resultMessage, resultCode }) => ({
    setiTagName,
    setiVariantName,
    resultMessage,
    resultCode,
  }))

  const { isMobile, resolvedClientIP, requestTime } = webstats ?? {}
  const { id, sourceId, clickId } = referral ?? {}

  const experimentMetadata = {
    appCode: isMobile ? 'MOBILEWEB' : 'DESKTOP',
    globalPartnerCode: 'PCLN',
    productID: 1,
    productSubType: 'ALL',
    plfCode: 'PCLN',
    clientIP: resolvedClientIP,
    serverTimestamp: requestTime,
    referralID: id ?? '',
    referralSourceID: sourceId ?? '',
    referralClickID: clickId ?? '',
    pageName: 'Flight Listings',
  }

  const config = {
    batchIntervalMS: 2000,
    batchSize: 20,
    useBatching: true,
    debugFireImpression: true,
    isIdleQueued: true,
    setiBaseExternalURL: globalThis.window === undefined ? '' : globalThis.window.location.origin,
  }

  pclnExpt.initialize(experimentDetails, experimentMetadata, config, seti)

  pclnExpt.fireEvent({
    eventType: 'pageViewEvent',
    pageName: 'Flight_react_listings_page',
  })

  // Configure logging library
  PCLNAnalytics.appName = appName
  PCLNAnalytics.appVersion = pclnBootstrapData?.appVersion

  // Tag the Trip Type
  tag('ITIN_TYPE', searchRequest?.params?.tripType, { newrelic: true })

  window.onbeforeunload = () => {
    pclnExpt.clearBatch()
  }
}

const customFetch = (uri, options) => {
  const { operationName } = JSON.parse(options.body)
  return fetch(getGraphURL({ endpoint: uri, operationName }), options)
}

const link = new HttpLink({ fetch: customFetch })

const client = new ApolloClient({
  uri: getGraphURL({ skipPclnGraph: pclnBootstrapData?.featureToggles?.flights?.skipPclnGraph }),
  cache: new InMemoryCache(),
  link,
  headers: {
    'apollographql-client-name': APOLLO_CLIENT_NAME,
    'apollographql-client-version': pclnBootstrapData?.appVersion || 'development',
  },
})

const variantLoader =
  pclnBootstrapData?.isNotReshopAndWLAndPackages &&
  pclnExpt.getTestState('AIR_SEARCHES_LOAD_MODIFICATION_WEB') === 'VARIANT'

hydrateRoot(
  document.getElementById('root'),
  <ApolloProvider client={client}>
    <BrowserRouter>
      <Provider store={store}>
        <ListingsClientV2
          variantLoader={variantLoader}
          pclnBootstrapData={pclnBootstrapData}
          appName={appName}
        />
      </Provider>
    </BrowserRouter>
  </ApolloProvider>
)

if (listingsMetaData && listings?.response) {
  logRetailPriceWithAPGData({
    searchRequest: pclnBootstrapData?.searchRequest,
    minimumRetailPrice: listingsMetaData?.minimumRetailPrice,
    tripType: listingsMetaData?.tripType,
  })
}
